import { useNotificationStore } from '../store/notificationStore';

/**
 * errorHandler - Gestion centralisée des erreurs API
 *
 * Élimine 31+ duplications de gestion d'erreurs dans les stores
 * Uniformise les messages et le logging
 *
 * @example
 * // Dans un store catch block
 * } catch (error: any) {
 *   handleApiError(error, 'la création du contrat');
 *   set({ isLoading: false });
 *   throw error;
 * }
 */

export function handleApiError(error: any, context: string): string {
  // Timeout client (axios) → message clair + NE PAS juste réessayer (le serveur bosse peut-être encore).
  const isTimeout = /timeout/i.test(error?.message || '') || error?.code === 'ECONNABORTED';
  const message = isTimeout
    ? "L'opération a pris trop de temps. Recharge la page pour vérifier si c'est bien passé AVANT de réessayer."
    : (error.response?.data?.message || error.response?.data?.error || `Erreur lors de ${context}`);

  console.error(`❌ Erreur ${context}:`, error);

  useNotificationStore.getState().addNotification({
    type: 'ERROR',
    title: 'Erreur',
    // Mention demandée par Thomas : toute erreur invite à screenshoter pour correction.
    message: `${message}\n📸 Fais une capture d'écran et envoie-la à Thomas pour correction.`
  });

  return message;
}

/**
 * Gestion centralisée des succès API
 *
 * @example
 * handleApiSuccess('Contrat créé avec succès', 'création du contrat');
 */
export function handleApiSuccess(message: string, context?: string) {
  if (context) {
    console.log(`✅ Succès ${context}:`, message);
  }

  useNotificationStore.getState().addNotification({
    type: 'SUCCESS',
    title: 'Succès',
    message
  });
}

/**
 * Gestion centralisée des avertissements
 *
 * @example
 * handleApiWarning('Le budget approche de la limite', 'vérification budget');
 */
export function handleApiWarning(message: string, context?: string) {
  if (context) {
    console.warn(`⚠️ Avertissement ${context}:`, message);
  }

  useNotificationStore.getState().addNotification({
    type: 'WARNING',
    title: 'Attention',
    message
  });
}

/**
 * Extraction du message d'erreur d'un objet error
 *
 * @example
 * const errorMessage = getErrorMessage(error);
 */
export function getErrorMessage(error: any, defaultMessage = 'Une erreur est survenue'): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  if (error?.message) {
    return error.message;
  }

  return defaultMessage;
}

/**
 * Vérifier si une erreur est une erreur réseau
 */
export function isNetworkError(error: any): boolean {
  return !error.response && error.message === 'Network Error';
}

/**
 * Vérifier si une erreur est une erreur d'authentification
 */
export function isAuthError(error: any): boolean {
  return error?.response?.status === 401 || error?.response?.status === 403;
}

/**
 * Vérifier si une erreur est une erreur de validation
 */
export function isValidationError(error: any): boolean {
  return error?.response?.status === 400 || error?.response?.status === 422;
}
